// TS twin of iOS `PlacementState.swift`.
//
// Mirrors Swift enums as discriminated unions; mirrors the manual-adjustment
// step constants bit-precisely so iOS and Android +/- buttons move the wheel
// by the same physical offset.
//
// ManualWheelPlacement here is the PURE DATA layer — the renderer-engine
// handles (RealityKit AnchorEntity / Three.js Group) are NOT carried on the
// twin. The WheelRenderer impl keeps its own Map<id, RendererHandle> for
// engine-specific lookup.

import type { WheelScale } from '@/models/wheel-scale'

// ── ARSession state ─────────────────────────────────────────────────────

export type ARSessionState =
  | { kind: 'initializing' }
  | { kind: 'scanning' }
  | { kind: 'ready' }
  | { kind: 'tracking' }
  | { kind: 'placing' }
  | { kind: 'placed' }
  | { kind: 'limited'; reason: string }
  | { kind: 'failed'; error: string }

export const arSessionState = {
  initializing: (): ARSessionState => ({ kind: 'initializing' }),
  scanning: (): ARSessionState => ({ kind: 'scanning' }),
  ready: (): ARSessionState => ({ kind: 'ready' }),
  tracking: (): ARSessionState => ({ kind: 'tracking' }),
  placing: (): ARSessionState => ({ kind: 'placing' }),
  placed: (): ARSessionState => ({ kind: 'placed' }),
  limited: (reason: string): ARSessionState => ({ kind: 'limited', reason }),
  failed: (error: string): ARSessionState => ({ kind: 'failed', error }),
}

// ── Manual placement phase ──────────────────────────────────────────────

export type ManualPlacementPhase =
  | { kind: 'idle' }
  | { kind: 'placing' }
  | { kind: 'selected'; wheelId: string }
  | { kind: 'adjusting'; wheelId: string }

export const manualPlacementPhase = {
  idle: (): ManualPlacementPhase => ({ kind: 'idle' }),
  placing: (): ManualPlacementPhase => ({ kind: 'placing' }),
  selected: (wheelId: string): ManualPlacementPhase => ({
    kind: 'selected',
    wheelId,
  }),
  adjusting: (wheelId: string): ManualPlacementPhase => ({
    kind: 'adjusting',
    wheelId,
  }),
}

// ── Manual wheel adjustments + step sizes ───────────────────────────────

export interface ManualWheelAdjustments {
  heightOffset: number
  depthOffset: number
  horizontalOffset: number
  rotationAngle: number
  tiltAngle: number
  yawAngle: number
  /** Multiplicative scale on top of the WheelScale axis vector — 1.0 =
   *  100 % of the size the WheelScale + template normalisation produce.
   *  User-controlled via the JoystickPad Scale mode. */
  scaleMultiplier: number
}

export const DEFAULT_MANUAL_WHEEL_ADJUSTMENTS: ManualWheelAdjustments = {
  heightOffset: 0,
  depthOffset: 0,
  horizontalOffset: 0,
  rotationAngle: 0,
  tiltAngle: 0,
  yawAngle: 0,
  scaleMultiplier: 1,
}

// Android-specific step sizes — 5× the original iOS parity values on
// both arrow buttons AND depth. Divergence intentional; iOS still runs
// the pre-bump values (0.002 / 0.005).
export const MANUAL_ADJUSTMENT_STEPS = {
  depth: 0.025,
  height: 0.010,
  horizontal: 0.010,
  rotation: Math.PI / 36,
  tilt: Math.PI / 36,
  yaw: Math.PI / 36,
  /** Multiplicative scale step per arrow press — 0.05 = 5 % per click. */
  scale: 0.05,
} as const

// ── Manual wheel placement (pure data layer) ────────────────────────────

export interface ManualWheelPlacement {
  readonly id: string
  surfaceNormal: readonly [number, number, number]
  baseScale: WheelScale
  currentScale: WheelScale
  depthOffset: number
  heightOffset: number
  horizontalOffset: number
  rotationAngle: number
  tiltAngle: number
  yawAngle: number
}
